import { Suspense, useState, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '@frontegg/react';
import { SideNavbar, TopNavbar } from './groupComponents';
import {
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';

import { getAmbientSoloRoutes } from 'src/modules/ambient_solo/Routes';
import Aura from '../Images/ambient.jpg';

import { getSuperAdminRoutes } from 'src/modules/SuperAdmin/Routes';
import { getTemplatesExchangeRoutes } from 'src/modules/TemplatesExchange/Routes';
import { getCelloWidgetRoutes } from 'src/modules/CelloWidget/Routes';
import { debounce } from 'lodash';
import { getCareConnectRoutes } from '../modules/appointments/Routes';
import { getSettingsRoutes } from '../modules/appointments/SettingsRoutes';
import { routes } from 'src/pages';

export const MasterLayout = () => {
  const { user } = useAuth();
  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);
  const { isEntitled: hasAmbientAccess } = useFeatureEntitlements('ambient');
  const { isEntitled: hasIntakeAccess } = useFeatureEntitlements('intake');
  const { isEntitled: hasFollowupAccess } = useFeatureEntitlements('followup');
  const { isEntitled: isPatientsModuleEntitled } =
    useFeatureEntitlements('patients-module');
  const { isEntitled: isFrontdeskModuleEntitled } =
    useFeatureEntitlements('frontdesk');
  const { isEntitled: isReferralsModuleEntitled } =
    useFeatureEntitlements('referral');
  const { isEntitled: isPhoneTriageModuleEntitled } =
    useFeatureEntitlements('phone-triage');
  const { isEntitled: hasCallReadAccess } =
    usePermissionEntitlements('calls.read');
  const isMobileOrTabletView = window.innerWidth < 1280;
  const [showSidebar, setShowSidebar] = useState(
    isMobileOrTabletView ? false : true
  );
  const [isMinimized, setIsMinimized] = useState(
    'minimizedSideNavBarPreference' in localStorage
      ? localStorage.getItem('minimizedSideNavBarPreference') === 'true'
      : window.innerWidth <= 1440
  );
  const handleToggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const onboarding_status = settingsOrgValue?.onboarding_complete;
  const superAdminAccess =
    (settingsOrgValue?.roles &&
      settingsOrgValue?.roles.includes('superadmin')) ||
    '';
  const celloAccess = settingsOrgValue?.cello_token;

  useEffect(() => {
    window.cello = window.cello || { cmd: [] };
    if (settingsOrgValue?.cello_token && user && !window.cello.cmd.length) {
      window.cello.cmd.push(async function (cello) {
        try {
          await cello.boot({
            productId: import.meta.env.VITE_CELLO_PRODUCT_ID,
            token: settingsOrgValue?.cello_token,
            language: 'en',
            productUserDetails: {
              firstName: settingsOrgValue.practitioner_first_name,
              lastName: settingsOrgValue.practitioner_last_name,
              fullName: settingsOrgValue.practitioner_name,
              email: user.email,
            },
          });
        } catch (error) {
          console.error('Failed to boot cello:', error);
        }
      });
    }
  }, [settingsOrgValue, user]);

  const handleResize = useMemo(() => {
    return debounce(() => {
      if (window.innerWidth < 1280) {
        setShowSidebar(false);
      } else if (window.innerWidth >= 1280) {
        setShowSidebar(true);
      }
    }, 100);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
    // TODO: Fix react useeffect dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getIndexRoute = () => {
    if (hasIntakeAccess || hasFollowupAccess) {
      return '/appointments';
    }
    if (hasAmbientAccess) {
      return '/encounters';
    }
    if (isReferralsModuleEntitled) {
      return '/referrals';
    }
    if (isPhoneTriageModuleEntitled) {
      return '/phone-triage';
    }
    if (isFrontdeskModuleEntitled) {
      return '/frontdesk';
    }
    if (isPatientsModuleEntitled) {
      return '/patients';
    }
    return '/settings';
  };

  return (
    <div
      className={`master-layout ${
        isMinimized ? 'master-layout--nav-minimized' : ''
      }`}
      style={{ backgroundImage: `url(${Aura})` }}
    >
      {onboarding_status ? (
        <SideNavbar
          showSidebar={showSidebar}
          handleToggleSidebar={handleToggleSidebar}
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
        />
      ) : (
        ''
      )}
      {onboarding_status ? (
        <TopNavbar
          handleToggleSidebar={handleToggleSidebar}
          isMinimized={isMinimized}
        />
      ) : null}
      <Suspense
        fallback={
          <div className="fallback d-flex align-items-center justify-content-center w-100 h-100">
            <span className="sr-only">Loading...</span>
          </div>
        }
      >
        <Routes>
          {(hasIntakeAccess || hasFollowupAccess) &&
            getCareConnectRoutes({
              isMinimized,
            })}
          {hasAmbientAccess &&
            getAmbientSoloRoutes({
              isMinimized,
              setIsMinimized,
            })}
          {superAdminAccess &&
            getSuperAdminRoutes({
              isMinimized,
            })}
          {getSettingsRoutes({
            isMinimized,
          })}
          {getTemplatesExchangeRoutes({
            isMinimized,
          })}
          {celloAccess &&
            getCelloWidgetRoutes({
              isMinimized,
            })}
          {routes({
            isPatientsModuleEntitled,
            isFrontdeskModuleEntitled,
            isReferralsModuleEntitled,
            isPhoneTriageModuleEntitled:
              isPhoneTriageModuleEntitled && hasCallReadAccess,
            isTagModuleEntitled:
              isPhoneTriageModuleEntitled ||
              superAdminAccess ||
              isReferralsModuleEntitled,
          })}
          <Route path="*" element={<Navigate replace to={getIndexRoute()} />} />
        </Routes>
      </Suspense>
    </div>
  );
};
